//-- Copyright (c) 2021 TMT Insights Group, LLC
import { globallyAvailableSnackBar } from '@/components/SnackBar';
import { PolarisApiResponse } from '@/redux/slices/slice_types';
import { Store } from '@reduxjs/toolkit';
import axiosOriginal, { AxiosError, AxiosResponse } from 'axios';
import { doLogout as doLogoutAction } from '@/redux/slices/authSlice';
declare module 'axios' {
  export interface AxiosRequestConfig {
    ignoredGlobalCatch?: boolean;
  }
}

type DoLogoutType = typeof doLogoutAction;
let store: Store, doLogout: DoLogoutType;

export const injectStore = (_store: Store) => {
  store = _store;
};

export const injectDoLogout = (_doLogout: DoLogoutType) => {
  doLogout = _doLogout;
};

const axios = axiosOriginal.create({
  baseURL: '/api/v1/'
});
// axios.defaults.timeout = 1000 * 60 * 5; // 3 minutes

const interceptorLogoutIfUnauthorized = [
  function (response: AxiosResponse) {
    // 2xx are not intercepted
    return response;
  },
  function (error: AxiosError<PolarisApiResponse<any>>) {
    const httpErrorCode = error.response?.status || 0;
    if (error.config?.ignoredGlobalCatch) return Promise.reject(error.response);
    if (error.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    } else if (httpErrorCode === 401) {
      store.dispatch(doLogout());
    } else if (httpErrorCode === 403) {
      const msg = (error.response?.data as any).detail || "You don't have the permissions for that action.";
      globallyAvailableSnackBar?.error?.(msg);
    } else if (httpErrorCode === 400) {
      let msg = (error.response?.data as any).detail || error.response?.data?.message;
      if (!msg || typeof msg !== 'string') {
        msg = 'There was a problem with that request.';
        console.error(error.response?.data);
      }
      globallyAvailableSnackBar?.error?.(msg);
    } else if (httpErrorCode === 500) {
      const msg = error.response?.data?.message || (error.response?.data as any).detail || 'There was a server error.';
      globallyAvailableSnackBar?.error?.(msg);
    } else if (error.message?.includes('timeout')) {
      globallyAvailableSnackBar?.error?.('The request timed out.');
    }
    return Promise.reject(error.response);
  }
];

const interceptors = [interceptorLogoutIfUnauthorized];

function setInterceptors() {
  interceptors.map((interceptor) => axios.interceptors.response.use(...(interceptor as any)));
}

function unSetInterceptors() {
  interceptors.map((interceptor) => axios.interceptors.response.eject(interceptor as any));
}

export const setToken = (token: string) => {
  localStorage.setItem('token', token);
  setInterceptors();
  axios.defaults.headers.common['Authorization'] = `Token ${token}`;
};

export const unsetToken = () => {
  localStorage.removeItem('token');
  unSetInterceptors();
  delete axios.defaults.headers.common['Authorization'];
};

const currentToken = localStorage.getItem('token');
if (currentToken) {
  setToken(currentToken);
}

export default axios;
